import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Printer, Download, FileSpreadsheet } from "lucide-react";

import { formatDMY } from "@/lib/dmy";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (val) => {
  if (!val) return "₹0";
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)}Cr`;
  if (val >= 100000) return `₹${(val / 100000).toFixed(2)}L`;
  if (val >= 1000) return `₹${(val / 1000).toFixed(1)}K`;
  return `₹${Math.round(val).toLocaleString("en-IN")}`;
};

const SECTION_TITLES = {
  sheets: "Sheet Breakdown",
  clients: "Client Breakdown",
  high_alerts: "High Value Alerts",
  low_alerts: "Low Value Alerts",
  cross_month: "Cross-Month Orders",
  packing_mismatch: "Packing Mismatch",
  errors: "Error Details",
  upload_history: "Upload History",
};

const exportCSV = (title, headers, rows, filename) => {
  let csv = `${title}\n${headers.join(",")}\n`;
  rows.forEach(r => { csv += r.map(c => typeof c === "string" && c.includes(",") ? `"${c}"` : c).join(",") + "\n"; });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = filename;
  a.click();
  toast.success("CSV exported");
};

const SectionPrintPage = () => {
  const [searchParams] = useSearchParams();
  const year = parseInt(searchParams.get("year") || "2025");
  const month = parseInt(searchParams.get("month") || "6");
  const section = searchParams.get("section") || "sheets";
  const title = SECTION_TITLES[section] || section;
  usePageTitle(`${title} — ${month}/${year}`);

  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/reports/month-detail?year=${year}&month=${month}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("Failed");
        setData(await res.json());
      } catch (e) { toast.error(e.message); }
      finally { setLoading(false); }
    })();
  }, [token, year, month]);

  if (loading) return <div className="p-8"><Skeleton className="h-10 w-80" /><Skeleton className="h-64 mt-4" /></div>;
  if (!data) return <div className="p-8 text-slate-500">No data</div>;

  const sm = data.summary;

  const handleCSV = () => {
    if (section === "sheets") {
      exportCSV(`${data.month_name} ${year} — Sheet Breakdown`, ["Sheet","Parcels","Packing%","Costed","Errors","TotalCost"],
        data.sheets.map(s => [s.name, s.parcel_count, s.packing_pct, s.with_costing, s.with_errors, s.total_cost]),
        `${data.month_name}-${year}-sheets.csv`);
    } else if (section === "clients") {
      exportCSV(`${data.month_name} ${year} — Client Breakdown`, ["Client","Parcels","TotalCost"],
        data.clients.map(c => [c.code, c.parcels, c.total_cost]),
        `${data.month_name}-${year}-clients.csv`);
    } else if (section === "high_alerts") {
      exportCSV(`${data.month_name} ${year} — High Value`, ["Tracking","Client","Product","Qty","Medicine","Shipping","Total","Flags"],
        (data.price_alerts?.high || []).map(o => [o.tracking, o.client, o.product, o.qty, o.medicine, o.shipping, o.total, (o.flags||[]).join("; ")]),
        `${data.month_name}-${year}-high-value.csv`);
    } else if (section === "low_alerts") {
      exportCSV(`${data.month_name} ${year} — Low Value`, ["Tracking","Client","Product","Qty","Medicine","Shipping","Total","Flags"],
        (data.price_alerts?.low || []).map(o => [o.tracking, o.client, o.product, o.qty, o.medicine, o.shipping, o.total, (o.flags||[]).join("; ")]),
        `${data.month_name}-${year}-low-value.csv`);
    } else if (section === "cross_month") {
      const rows = [];
      (data.cross_month_orders?.months || []).forEach(cm => cm.orders.forEach(o => rows.push([cm.year_month, o.tracking, o.product, o.client, o.date, o.sheet])));
      exportCSV(`${data.month_name} ${year} — Cross-Month`, ["TargetMonth","Tracking","Product","Client","Date","Sheet"], rows, `${data.month_name}-${year}-cross-month.csv`);
    } else if (section === "errors") {
      const rows = [];
      (data.error_details || []).forEach(e => (e.errors||[]).forEach(err => rows.push([e.tracking, e.product, e.client, err])));
      exportCSV(`${data.month_name} ${year} — Errors`, ["Tracking","Product","Client","Error"], rows, `${data.month_name}-${year}-errors.csv`);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 print:p-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 print:hidden">
        <div>
          <h1 className="text-lg font-bold text-slate-800">{data.month_name} {year} — {title}</h1>
          <p className="text-xs text-slate-500">{sm.total_parcels.toLocaleString()} total parcels | Generated: {new Date().toLocaleString()}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1" />Print</Button>
          <Button variant="outline" size="sm" onClick={handleCSV}><FileSpreadsheet className="w-4 h-4 mr-1" />CSV</Button>
        </div>
      </div>
      {/* Print header */}
      <div className="hidden print:block text-center mb-4">
        <h1 className="text-base font-bold">{data.month_name} {year} — {title}</h1>
        <p className="text-xs text-gray-500">Parcels: {sm.total_parcels} | Generated: {new Date().toLocaleString()}</p>
      </div>

      {/* Section Content */}
      {section === "sheets" && (
        <table className="w-full text-sm border border-slate-200">
          <thead className="bg-slate-100"><tr>
            <th className="px-3 py-2 text-left">Sheet</th><th className="px-3 py-2 text-right">Parcels</th>
            <th className="px-3 py-2 text-right">Packing</th><th className="px-3 py-2 text-right">Costed</th>
            <th className="px-3 py-2 text-right">Errors</th><th className="px-3 py-2 text-right">Total Cost</th>
          </tr></thead>
          <tbody>
            {data.sheets.map(s => (
              <tr key={s.name} className="border-t"><td className="px-3 py-2 font-medium">{s.name}</td>
                <td className="px-3 py-2 text-right">{s.parcel_count}</td><td className="px-3 py-2 text-right">{s.packing_pct}%</td>
                <td className="px-3 py-2 text-right">{s.with_costing}</td><td className="px-3 py-2 text-right">{s.with_errors}</td>
                <td className="px-3 py-2 text-right font-mono">{fmt(s.total_cost)}</td></tr>
            ))}
            <tr className="border-t-2 font-semibold bg-slate-50"><td className="px-3 py-2">Total</td>
              <td className="px-3 py-2 text-right">{sm.total_parcels}</td><td className="px-3 py-2 text-right">{sm.packing_pct}%</td>
              <td className="px-3 py-2 text-right">{sm.with_costing}</td><td className="px-3 py-2 text-right">{sm.with_errors}</td>
              <td className="px-3 py-2 text-right font-mono">{fmt(sm.total_cost)}</td></tr>
          </tbody>
        </table>
      )}

      {section === "clients" && (
        <table className="w-full text-sm border border-slate-200">
          <thead className="bg-slate-100"><tr>
            <th className="px-3 py-2 text-left">Client</th><th className="px-3 py-2 text-right">Parcels</th>
            <th className="px-3 py-2 text-right">Total Cost</th><th className="px-3 py-2 text-right">Share</th>
          </tr></thead>
          <tbody>
            {data.clients.map(c => (
              <tr key={c.code} className="border-t"><td className="px-3 py-2 font-medium">{c.code}</td>
                <td className="px-3 py-2 text-right">{c.parcels}</td><td className="px-3 py-2 text-right font-mono">{fmt(c.total_cost)}</td>
                <td className="px-3 py-2 text-right">{sm.total_parcels ? Math.round(c.parcels/sm.total_parcels*100) : 0}%</td></tr>
            ))}
          </tbody>
        </table>
      )}

      {(section === "high_alerts" || section === "low_alerts") && (() => {
        const items = section === "high_alerts" ? data.price_alerts?.high : data.price_alerts?.low;
        const color = section === "high_alerts" ? "orange" : "sky";
        return (
          <table className="w-full text-sm border border-slate-200">
            <thead className="bg-slate-100"><tr>
              <th className="px-3 py-2 text-left">Tracking</th><th className="px-3 py-2 text-left">Client</th>
              <th className="px-3 py-2 text-left">Product</th><th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Medicine</th><th className="px-3 py-2 text-right">Shipping</th>
              <th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2 text-left">Flags</th>
            </tr></thead>
            <tbody>
              {(items || []).map((o, i) => (
                <tr key={i} className="border-t"><td className="px-3 py-2 font-mono text-xs">{o.tracking}</td>
                  <td className="px-3 py-2 font-medium">{o.client}</td><td className="px-3 py-2 text-xs">{o.product}</td>
                  <td className="px-3 py-2 text-right">{o.qty}</td><td className="px-3 py-2 text-right font-mono">{fmt(o.medicine)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmt(o.shipping)}</td>
                  <td className={`px-3 py-2 text-right font-mono font-semibold text-${color}-700`}>{fmt(o.total)}</td>
                  <td className="px-3 py-2">{o.flags?.map((f,j) => <Badge key={j} className={`text-[10px] bg-${color}-100 text-${color}-700 mr-1`}>{f}</Badge>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      })()}

      {section === "cross_month" && data.cross_month_orders?.months?.map(cm => (
        <div key={cm.year_month} className="mb-4">
          <h3 className="text-sm font-semibold mb-2 text-amber-800">{cm.year_month} — {cm.count} orders</h3>
          <table className="w-full text-sm border border-slate-200">
            <thead className="bg-slate-100"><tr>
              <th className="px-3 py-2 text-left">Tracking</th><th className="px-3 py-2 text-left">Product</th>
              <th className="px-3 py-2 text-left">Client</th><th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Sheet</th>
            </tr></thead>
            <tbody>
              {cm.orders.map((o, i) => (
                <tr key={i} className="border-t"><td className="px-3 py-2 font-mono text-xs">{o.tracking}</td>
                  <td className="px-3 py-2 text-xs">{o.product}</td><td className="px-3 py-2 font-medium">{o.client}</td>
                  <td className="px-3 py-2 font-mono text-xs">{formatDMY(o.date)}</td><td className="px-3 py-2 text-xs">{o.sheet}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {section === "packing_mismatch" && (
        <table className="w-full text-sm border border-slate-200">
          <thead className="bg-slate-100"><tr>
            <th className="px-3 py-2 text-left">Tracking</th><th className="px-3 py-2 text-left">Products</th>
            <th className="px-3 py-2 text-left">Service</th><th className="px-3 py-2 text-right">Weight</th><th className="px-3 py-2 text-left">Customer</th>
          </tr></thead>
          <tbody>
            {(data.packing_mismatch?.entries || []).map((e, i) => (
              <tr key={i} className="border-t"><td className="px-3 py-2 font-mono text-xs text-red-600">{e.tracking}</td>
                <td className="px-3 py-2 text-xs">{Array.isArray(e.products) ? e.products.join(", ") : e.products}</td>
                <td className="px-3 py-2">{e.service}</td><td className="px-3 py-2 text-right font-mono">{e.weight}g</td>
                <td className="px-3 py-2">{e.customer}</td></tr>
            ))}
          </tbody>
        </table>
      )}

      {section === "errors" && (
        <table className="w-full text-sm border border-slate-200">
          <thead className="bg-slate-100"><tr>
            <th className="px-3 py-2 text-left">Tracking</th><th className="px-3 py-2 text-left">Product</th>
            <th className="px-3 py-2 text-left">Client</th><th className="px-3 py-2 text-left">Error</th>
          </tr></thead>
          <tbody>
            {(data.error_details || []).map((e, i) => (
              <tr key={i} className="border-t"><td className="px-3 py-2 font-mono text-xs">{e.tracking}</td>
                <td className="px-3 py-2 text-xs">{e.product}</td><td className="px-3 py-2 font-medium">{e.client}</td>
                <td className="px-3 py-2">{e.errors?.map((err, j) => <Badge key={j} variant="destructive" className="text-[10px] mr-1">{err}</Badge>)}</td></tr>
            ))}
          </tbody>
        </table>
      )}

      {section === "upload_history" && (
        <table className="w-full text-sm border border-slate-200">
          <thead className="bg-slate-100"><tr>
            <th className="px-3 py-2 text-left">Type</th><th className="px-3 py-2 text-left">Filename</th>
            <th className="px-3 py-2 text-left">Uploaded By</th><th className="px-3 py-2 text-left">Date</th>
          </tr></thead>
          <tbody>
            {(data.upload_history || []).map((h, i) => (
              <tr key={i} className="border-t"><td className="px-3 py-2"><Badge variant="outline">{h.type}</Badge></td>
                <td className="px-3 py-2 font-mono text-xs">{h.filename || "-"}</td>
                <td className="px-3 py-2">{h.uploaded_by || "-"}</td>
                <td className="px-3 py-2 text-xs">{h.uploaded_at ? new Date(h.uploaded_at).toLocaleString() : "-"}</td></tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default SectionPrintPage;
